import {
  EffectComposer,
  Bloom,
  Vignette,
  Noise,
  ChromaticAberration,
  HueSaturation,
  BrightnessContrast,
  SMAA,
} from '@react-three/postprocessing'
import { BlendFunction, KernelSize } from 'postprocessing'
import { useMemo } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { useExperienceStore } from '../store/experienceStore'
import { QUALITY_PRESETS } from '../config/quality'

/**
 * Film-look stack: soft bloom, anamorphic-ish chroma when time moves,
 * grain, vignette, mild teal-orange grade.
 */
export function PostFX() {
  const { size } = useThree()
  const quality = useExperienceStore((s) => s.quality)
  const timeSpeed = useExperienceStore((s) => s.timeSpeed)
  const traverseIntensity = useExperienceStore((s) => s.traverseIntensity)
  const cfg = QUALITY_PRESETS[quality]

  const speedAbs = Math.abs(timeSpeed)
  const energy = Math.min(1, speedAbs * 3 + traverseIntensity)

  const bloomIntensity = 0.4 + energy * 0.65
  const chroma = useMemo(() => new THREE.Vector2(0, 0), [])
  // Horizontal stretch like anamorphic lens when time flows
  chroma.set(0.0004 + energy * 0.0035, 0.00015 + energy * 0.001)

  if (size.width < 64 || size.height < 64) return null

  return (
    <EffectComposer
      multisampling={quality === 'high' ? 4 : quality === 'medium' ? 2 : 0}
      enableNormalPass={false}
    >
      {quality !== 'low' ? <SMAA /> : <></>}

      {cfg.bloom ? (
        <Bloom
          intensity={bloomIntensity}
          luminanceThreshold={0.42}
          luminanceSmoothing={0.7}
          mipmapBlur
          kernelSize={KernelSize.MEDIUM}
          radius={0.65}
        />
      ) : (
        <></>
      )}

      <HueSaturation
        blendFunction={BlendFunction.NORMAL}
        hue={0.015}
        saturation={0.08 + energy * 0.05}
      />
      <BrightnessContrast
        brightness={0.0}
        contrast={0.06 + energy * 0.04}
      />

      {cfg.afterimage ? (
        <ChromaticAberration
          blendFunction={BlendFunction.NORMAL}
          offset={chroma}
          radialModulation
          modulationOffset={0.35}
        />
      ) : (
        <></>
      )}

      <Noise
        premultiply
        blendFunction={BlendFunction.SOFT_LIGHT}
        opacity={0.045 + energy * 0.04}
      />

      <Vignette
        offset={0.18}
        darkness={0.62 + energy * 0.1}
        blendFunction={BlendFunction.NORMAL}
      />
    </EffectComposer>
  )
}
